<template>
  <div class="subject-detail-page">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 面包屑 + 标题 -->
      <div class="mb-6">
        <nav class="text-sm text-neutral-400 mb-2">
          <router-link to="/subjects" class="hover:text-primary"
            >学科导航</router-link
          >
          <span class="mx-1">/</span>
          <span class="text-neutral-600">{{
            subject ? subject.title : ""
          }}</span>
          <template v-for="p in crumb" :key="p.id">
            <span class="mx-1">/</span>
            <span class="text-neutral-600">{{ p.title }}</span>
          </template>
          <template v-if="selectedNode">
            <span class="mx-1">/</span>
            <span class="text-primary">{{ selectedNode.title }}</span>
          </template>
        </nav>
        <div class="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 class="text-3xl font-bold">
              {{ subject ? subject.title : "学科" }}
              <span class="text-lg font-normal text-neutral-400 ml-1"
                >教学工具体系</span
              >
            </h1>
            <p class="text-neutral-500 mt-1">
              按年段、年级、学期、单元逐级筛选，每个工具都标注了对应的教学位置
            </p>
          </div>
          <div class="text-sm text-neutral-500">
            共 <b class="text-primary">{{ total }}</b> 个工具
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row gap-6">
        <!-- 左侧：知识体系列表 -->
        <aside class="lg:w-72 flex-shrink-0">
          <div class="lg:sticky lg:top-20">
            <div
              class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
            >
              <div
                class="px-4 py-3 border-b border-neutral-100 flex items-center justify-between"
              >
                <span class="font-medium text-sm">知识体系</span>
                <button
                  v-if="selectedNode"
                  @click="selectNode(null)"
                  class="text-xs text-primary hover:underline"
                >
                  重置
                </button>
              </div>

              <div class="p-2 max-h-[70vh] overflow-y-auto">
                <!-- 全部 -->
                <div
                  @click="selectNode(null)"
                  class="px-3 py-2 rounded-lg text-sm cursor-pointer mb-1 flex items-center justify-between"
                  :class="
                    !selectedNode
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'hover:bg-neutral-50 text-neutral-700'
                  "
                >
                  <span>全部{{ subject ? subject.title : "" }}工具</span>
                  <span class="text-xs text-neutral-400">{{
                    subject ? subject.toolCount || 0 : 0
                  }}</span>
                </div>

                <div v-if="treeLoading" class="text-center py-6">
                  <i class="fa fa-spinner fa-spin text-primary"></i>
                </div>

                <div v-else-if="tree.length === 0" class="px-3 py-6 text-center">
                  <p class="text-xs text-neutral-400">该学科暂未配置知识体系</p>
                  <p class="text-xs text-neutral-400 mt-1">仍可浏览全部工具</p>
                </div>

                <!-- 层级列表（按展开状态摊平渲染） -->
                <div v-else>
                  <div
                    v-for="n in visibleNodes"
                    :key="n.id"
                    class="flex items-center rounded-lg text-sm cursor-pointer"
                    :class="
                      selectedNode && selectedNode.id === n.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-neutral-50 text-neutral-700'
                    "
                    :style="{ paddingLeft: n.depth * 14 + 6 + 'px' }"
                  >
                    <span
                      @click.stop="toggleNode(n)"
                      class="w-5 h-5 flex items-center justify-center flex-shrink-0 text-neutral-400"
                    >
                      <i
                        v-if="n.children && n.children.length"
                        class="fa text-xs"
                        :class="
                          expanded[n.id] ? 'fa-caret-down' : 'fa-caret-right'
                        "
                      ></i>
                    </span>
                    <span
                      @click.stop="selectNode(n)"
                      class="flex-1 py-1.5 pr-1 truncate"
                      :title="n.title"
                      >{{ n.title }}</span
                    >
                    <span class="text-xs text-neutral-300 pr-2">{{
                      typeName(n.type)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <!-- 右侧：工具列表 -->
        <section class="flex-1 min-w-0">
          <!-- 当前筛选位置 -->
          <div
            v-if="selectedNode"
            class="mb-4 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 flex items-center justify-between"
          >
            <div class="text-sm">
              <span class="text-neutral-500">当前位置：</span>
              <span class="font-medium text-neutral-800">
                {{ subject ? subject.title : "" }}
                <template v-for="p in crumb" :key="p.id">
                  · {{ p.title }}
                </template>
                <template v-if="selectedNode">
                  · {{ selectedNode.title }}
                </template>
              </span>
            </div>
            <button
              v-if="selectedParent"
              @click="selectNode(selectedParent)"
              class="text-xs text-primary hover:underline flex-shrink-0 ml-3"
            >
              返回上一级
            </button>
            <button
              v-else
              @click="selectNode(null)"
              class="text-xs text-primary hover:underline flex-shrink-0 ml-3"
            >
              返回全部
            </button>
          </div>

          <div v-if="loading" class="text-center py-20">
            <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
          </div>

          <div
            v-else-if="tools.length > 0"
            class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
          >
            <div
              v-for="tool in tools"
              :key="tool.id"
              class="bg-white rounded-xl border border-neutral-200 overflow-hidden hover-lift transition-all duration-300 cursor-pointer flex flex-col"
              @click="openToolDetail(tool)"
            >
              <div class="h-40 overflow-hidden bg-neutral-100 relative">
                <img
                  :src="tool.cover"
                  :alt="tool.title"
                  class="w-full h-full object-cover"
                />
              </div>
              <div class="p-4 flex-1 flex flex-col">
                <h3 class="font-bold text-base mb-2 line-clamp-1">
                  {{ tool.title }}
                </h3>

                <!-- 学段 / 年级 / 学期 / 单元 标注 -->
                <div
                  v-if="tagsOf(tool).length"
                  class="flex flex-wrap gap-1 mb-2"
                >
                  <span
                    v-for="(t, i) in tagsOf(tool)"
                    :key="i"
                    class="px-2 py-0.5 rounded text-xs"
                    :class="
                      t.type === 'unit'
                        ? 'bg-green-50 text-green-700'
                        : t.type === 'category'
                        ? 'bg-neutral-100 text-neutral-600'
                        : 'bg-primary/10 text-primary'
                    "
                  >
                    {{ t.title }}
                  </span>
                </div>
                <div v-else class="mb-2">
                  <span
                    class="px-2 py-0.5 rounded text-xs bg-neutral-100 text-neutral-400"
                    >未标注教学位置</span
                  >
                </div>

                <p
                  class="text-neutral-500 text-sm mb-3 line-clamp-2 flex-1"
                >
                  {{ tool.description }}
                </p>

                <div
                  class="flex items-center justify-between pt-3 border-t border-neutral-100 text-xs text-neutral-400"
                >
                  <div class="flex items-center space-x-3">
                    <span
                      ><i class="fa fa-download mr-1"></i>
                      {{ formatNumber(tool.downloadCount) }}</span
                    >
                    <span
                      ><i class="fa fa-star text-yellow-400 mr-1"></i>
                      {{ tool.rate || 0 }}</span
                    >
                  </div>
                  <span
                    class="truncate max-w-[7rem]"
                    :title="tool.authorName || tool.uname"
                  >
                    {{ tool.authorName || tool.uname }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-20">
            <div class="inline-block p-4 rounded-full bg-neutral-100 mb-4">
              <i class="fa fa-book text-neutral-400 text-2xl"></i>
            </div>
            <h3 class="text-lg font-medium text-neutral-900 mb-1">暂无工具</h3>
            <p class="text-neutral-500">
              该位置下暂时没有工具，试试切换其他年段或单元。
            </p>
          </div>

          <div v-if="tools.length > 0" class="flex justify-end mt-6">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="pageChange"
            />
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { globalState } from "@/state";

export default {
  name: "SubjectDetailPage",
  data() {
    return {
      subjectId: "",
      subject: null,
      subjects: [],
      nodes: [], // edu/list 全量知识节点
      nodeMap: {}, // id -> node
      tree: [], // 以当前学科为根的树
      expanded: {}, // 节点展开状态
      treeLoading: false,
      selectedNode: null,
      selectedParent: null,
      crumb: [],
      tools: [],
      categories: [],
      loading: false,
      page: 1,
      pageSize: 12,
      total: 0,
    };
  },
  computed: {
    /** 按展开状态把树摊平成带层级的可见列表（避免递归组件） */
    visibleNodes() {
      const out = [];
      const walk = (nodes, depth) => {
        nodes.forEach((n) => {
          out.push({ ...n, depth });
          if (this.expanded[n.id] && n.children && n.children.length) {
            walk(n.children, depth + 1);
          }
        });
      };
      walk(this.tree, 0);
      return out;
    },
  },
  created() {
    this.subjectId = this.$route.params.id || this.$route.query.subject || "";
    this.getSubjects();
    this.getEduNodes();
    this.$axios({ url: "category/list/count" }).then((res) => {
      this.categories = res.data || [];
    });
  },
  watch: {
    "$route.params.id"(val) {
      if (val && val !== this.subjectId) {
        this.subjectId = val;
        this.selectedNode = null;
        this.selectedParent = null;
        this.crumb = [];
        this.expanded = {};
        this.page = 1;
        this.subject =
          this.subjects.find((s) => s.id == this.subjectId) || null;
        this.buildTree();
        this.loadTools();
      }
    },
  },
  methods: {
    typeName(type) {
      return (
        { stage: "年段", grade: "年级", term: "学期", unit: "单元" }[type] || ""
      );
    },
    getSubjects() {
      this.$axios({ url: "subinfo/list/count" }).then((res) => {
        this.subjects = res.data || [];
        this.subject =
          this.subjects.find((s) => s.id == this.subjectId) || null;
      });
    },
    getEduNodes() {
      this.treeLoading = true;
      this.$axios({ url: "edu/list" })
        .then((res) => {
          this.nodes = res.data || [];
          const map = {};
          this.nodes.forEach((n) => {
            map[n.id] = n;
          });
          this.nodeMap = map;
          this.buildTree();
        })
        .catch(() => {})
        .finally(() => {
          this.treeLoading = false;
          this.loadTools();
        });
    },
    /** 以当前学科为根，按 parentId 组装成树 */
    buildTree() {
      const byParent = {};
      this.nodes.forEach((n) => {
        (byParent[n.parentId] = byParent[n.parentId] || []).push(n);
      });
      const build = (parentId, parent) =>
        (byParent[parentId] || [])
          .slice()
          .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
          .map((n) => {
            const node = { ...n, parent: parent || null, children: [] };
            node.children = build(n.id, node);
            return node;
          });
      this.tree = build(this.subjectId, null);
    },
    /** 面包屑：从选中节点往上追溯到学科 */
    buildCrumb(node) {
      const path = [];
      let cur = node;
      let guard = 0;
      while (
        cur &&
        cur.parentId &&
        cur.parentId !== this.subjectId &&
        guard++ < 10
      ) {
        const parent = this.nodeMap[cur.parentId];
        if (!parent) break;
        path.unshift(parent);
        cur = parent;
      }
      return path;
    },
    toggleNode(node) {
      if (!node.children || !node.children.length) return;
      // Vue 3 的 Proxy 响应式支持直接新增/修改对象属性
      this.expanded[node.id] = !this.expanded[node.id];
    },
    selectNode(node) {
      this.selectedNode = node || null;
      this.selectedParent = node ? node.parent : null;
      this.crumb = node ? this.buildCrumb(node) : [];
      // 选中时把路径上的父级都展开
      if (node) {
        let cur = node.parent;
        let guard = 0;
        while (cur && guard++ < 10) {
          this.expanded[cur.id] = true;
          cur = cur.parent;
        }
        if (node.children && node.children.length) {
          this.expanded[node.id] = true;
        }
      }
      this.page = 1;
      this.loadTools();
    },
    async loadTools() {
      this.loading = true;
      const body = {
        subId: this.subjectId || "all",
        page: this.page,
        pageSize: this.pageSize,
      };
      if (this.selectedNode) {
        body[this.selectedNode.type + "Id"] = this.selectedNode.id;
      }
      try {
        const data = await this.$aesEncrypt(body);
        const res = await this.$axios({
          url: "aitool/search",
          method: "POST",
          data: { data },
        });
        if (res.code == 200) {
          this.total = res.data.total;
          this.tools = res.data.list || [];
        }
      } catch (e) {
        this.tools = [];
      } finally {
        this.loading = false;
      }
    },
    pageChange(page) {
      this.page = page;
      this.loadTools();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    /** 工具卡片上的位置标注：年段 > 年级 > 学期 > 单元，最后补分类 */
    tagsOf(tool) {
      const tags = [];
      ["stageId", "gradeId", "termId", "unitId"].forEach((key) => {
        const id = tool[key];
        const node = id && this.nodeMap[id];
        if (node) {
          tags.push({ type: node.type, title: node.title });
        }
      });
      if (tool.categoryName) {
        tags.push({ type: "category", title: tool.categoryName });
      }
      return tags;
    },
    openToolDetail(tool) {
      globalState.openModal(tool.id, this.categories);
    },
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num || 0;
    },
  },
};
</script>
